"use client";
import { motion } from "framer-motion";
import { CheckCircle, Target, Users, Award, Lightbulb, TrendingUp } from "lucide-react";

const About = () => {
  const values = [
    { 
      icon: Target, 
      title: "Enfoque Estratégico", 
      description: "Cada proyecto alineado con los objetivos de tu negocio"
    },
    { 
      icon: Users, 
      title: "Colaboración Directa", 
      description: "Trabajamos como una extensión de tu equipo interno"
    },
    { 
      icon: Award, 
      title: "Calidad Garantizada", 
      description: "Estrictos controles de calidad en cada entrega"
    },
    { 
      icon: TrendingUp, 
      title: "Resultados Medibles", 
      description: "Impacto cuantificable en la eficiencia operativa"
    },
  ];

  const achievements = [
    "15+ años transformando empresas con tecnología",
    "50+ proyectos exitosos en diferentes industrias",
    "Procesos de desarrollo ágiles y transparentes",
    "Soporte 24/7 para nuestros clientes"
  ];

  const team = [
    {
      name: "Esteban Galarza",
      position: "Fundador & Director General",
      image: "/placeholder.svg?height=80&width=80",
    },
    {
      name: "Fabian Planchon",
      position: "Director de Ingeniería",
      image: "/placeholder.svg?height=80&width=80",
    },
    {
      name: "Francisco Galarza",
      position: "Desarrollador Full-Stack",
      image: "/placeholder.svg?height=80&width=80",
    },
    {
      name: "Jeremías Marín",
      position: "Desarrollador de Automatización",
      image: "/placeholder.svg?height=80&width=80",
    },
  ];

  return (
    <section id="nosotros" className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Elementos decorativos con identidad Mayma */}
      <div className="absolute top-10 right-10 w-32 h-32 bg-[#77c593]/8 rounded-full blur-2xl"></div>
      <div className="absolute bottom-20 left-10 w-48 h-48 bg-[#77c593]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-gradient-to-br from-[#77c593]/15 to-[#77c593]/10 p-3 rounded-xl">
                <Lightbulb size={24} className="text-[#77c593]" />
              </div>
              <span className="text-[#77c593] font-semibold uppercase tracking-wide text-sm">
                Quiénes Somos
              </span>
            </div>

            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Innovación <span className="bg-gradient-to-r from-[#77c593] to-[#5a9d73] bg-clip-text text-transparent">Tecnológica</span> a tu Servicio
            </h2>
            <div className="w-32 h-1.5 bg-gradient-to-r from-[#77c593] to-[#5a9d73] mb-8 rounded-full"></div>
            <div className="space-y-6 text-xl leading-relaxed text-gray-600 mb-10">
              <p>
                Somos especialistas en <strong className="text-[#77c593]">transformación digital empresarial</strong>. 
                Combinamos experiencia técnica con visión estratégica para crear soluciones 
                que escalan con tu negocio.
              </p>

              <p>
                Desde startups hasta corporaciones establecidas, ayudamos a empresas 
                a <strong className="text-[#77c593]">automatizar procesos</strong>, 
                optimizar operaciones y acelerar su crecimiento a través de la tecnología.
              </p>
            </div>

            <div className="space-y-4">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start space-x-4 p-4 bg-gradient-to-r from-[#77c593]/5 to-transparent rounded-xl border-l-4 border-[#77c593]/50"
                >
                  <CheckCircle
                    size={20}
                    className="text-[#77c593] flex-shrink-0 mt-1"
                  />
                  <span className="text-gray-700 font-medium text-lg">{achievement}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-[#77c593]/30 hover:shadow-xl transition-all duration-500 group relative overflow-hidden"
              >
                {/* Background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#77c593]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="bg-gradient-to-br from-[#77c593]/15 to-[#77c593]/10 p-4 rounded-2xl w-fit mb-6 group-hover:from-[#77c593] group-hover:to-[#5a9d73] transition-all duration-500">
                    <value.icon size={28} className="text-[#77c593] group-hover:text-white transition-colors duration-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#77c593] transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Sección del equipo */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Nuestro <span className="text-[#77c593]">Equipo</span>
          </h3>
          <div className="w-16 h-1 bg-[#77c593] mx-auto mb-8"></div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:border-[#77c593]/30 hover:shadow-lg text-center group"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className="relative mx-auto mb-4"
                >
                  <img
                    src={member.image || "/placeholder.svg"}
                    alt={member.name}
                    className="w-20 h-20 rounded-full mx-auto border-4 border-[#77c593]/20 group-hover:border-[#77c593]/40 transition-colors "
                  />
                  <div className="absolute inset-0 bg-[#77c593]/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity "></div>
                </motion.div>
                <h4 className="font-bold text-gray-900 mb-1">{member.name}</h4>
                <p className="text-[#77c593] font-semibold text-sm mb-2">
                  {member.position}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default About;
